import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

// ---------------------------------------------------------------------------
//  KAN-41 · Account servicing — producer-authenticated actions against the
//  /api/v1/accounts/{id}/* endpoints. Money terms are locked once an account
//  is active; these operations settle, stop, or log payments against the
//  existing balance — they never re-price the financing.
//
//  Same auth pattern as the other producer services: pull the bearer token
//  from AuthService and go through ApiService.getWithAuth / postWithAuth.
// ---------------------------------------------------------------------------

/** GET /accounts/{id}/payoff-quote — read-only current payoff figure. */
export interface PayoffQuote {
  quoteId: number;
  payoffAmount: number;
  remainingBalance: number;
  asOf: string;
}

/** POST /accounts/{id}/payoff — result of settling the balance in full. */
export interface PayoffResult {
  quoteId: number;
  paymentId: number;
  payoffAmount: number;
  status: string;
  message: string;
}

/** POST /accounts/{id}/cancel — stops billing; returns the estimated refund. */
export interface CancelResult {
  cancelled: boolean;
  estimatedUnearnedRefund: number;
  note: string;
}

/** POST /accounts/{id}/record-payment — body for logging an off-Stripe payment. */
export interface RecordPaymentRequest {
  amount: number;
  paymentType?: string;
  note?: string;
}

/** POST /accounts/{id}/record-payment — result of a logged manual payment. */
export interface RecordPaymentResult {
  paymentId: number;
  quoteId: number;
  amount: number;
  netAmount: number;
  paymentType: string;
  status: string;
  paymentDate: string;
}

/** GET /accounts/{id}/fees — an assessed fee (late / NSF / admin) on an account. */
export interface AccountFee {
  id: number;
  type: string;          // Late | NSF | Admin
  amount: number;
  assessedAt: string;
  stripeInvoiceId?: string;
  isWaived: boolean;
  isPaid: boolean;
  collectionStatus: 'Assessed' | 'Invoiced' | 'Paid' | 'Waived';
  waivedAt?: string;
  waiveReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountServicingService {
  private readonly accountsEndpoint = '/api/v1/accounts';

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  /**
   * GET /accounts/{id}/payoff-quote — fetch the current payoff amount for an
   * account (read-only; does not mutate anything).
   */
  getPayoffQuote(id: number): Observable<PayoffQuote> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.getWithAuth<PayoffQuote>(`${this.accountsEndpoint}/${id}/payoff-quote`, token);
  }

  /**
   * POST /accounts/{id}/payoff — settle the remaining balance in full. Records
   * a payoff payment and returns the resulting payment id + status.
   */
  payoff(id: number): Observable<PayoffResult> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.postWithAuth<PayoffResult>(`${this.accountsEndpoint}/${id}/payoff`, {}, token);
  }

  /**
   * POST /accounts/{id}/cancel — stop future billing and mark the account
   * Cancelled. Returns the estimated unearned-premium refund and a note about
   * how the refund is (or isn't) processed under the applicable CFL rule.
   */
  cancel(id: number): Observable<CancelResult> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.postWithAuth<CancelResult>(`${this.accountsEndpoint}/${id}/cancel`, {}, token);
  }

  /** GET /accounts/{id}/fees — list assessed fees (late / NSF / admin) on an account. */
  listFees(id: number): Observable<AccountFee[]> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.getWithAuth<AccountFee[]>(`${this.accountsEndpoint}/${id}/fees`, token);
  }

  /** POST /accounts/{id}/fees/{feeId}/waive — waive a fee (super-admin only; reason required, audit-logged). */
  waiveFee(id: number, feeId: number, reason: string): Observable<AccountFee> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.postWithAuth<AccountFee>(`${this.accountsEndpoint}/${id}/fees/${feeId}/waive`, { reason }, token);
  }

  /**
   * POST /accounts/{id}/record-payment — log a payment collected outside of
   * Stripe (check, wire, cash) against the account balance.
   */
  recordPayment(id: number, request: RecordPaymentRequest): Observable<RecordPaymentResult> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.postWithAuth<RecordPaymentResult>(`${this.accountsEndpoint}/${id}/record-payment`, request, token);
  }
}
